import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  Visibility as VisibilityIcon,
  Settings as SettingsIcon,
  AutoAwesome as AutoAwesomeIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import * as gapAnalysisService from '../../utils/gapAnalysisService';

const GapAnalysisPage = () => {
  const { projectId } = useParams();
  const { project } = useOutletContext();

  // State management
  const [loading, setLoading] = useState(false);
  const [backlogItems, setBacklogItems] = useState([]);
  const [workshopRequiredItems, setWorkshopRequiredItems] = useState([]);
  const [workshopItemsWithAttendees, setWorkshopItemsWithAttendees] = useState({});
  const [schedules, setSchedules] = useState([]);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [error, setError] = useState(null);

  // Schedule configuration (Step 2)
  const [workshopSettings, setWorkshopSettings] = useState(null);
  const [scheduleConfig, setScheduleConfig] = useState({
    start_date: '',
    end_date: ''
  });
  const [autoScheduling, setAutoScheduling] = useState(false);

  // Dialog states
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    scheduled_date: '',
    start_time: '',
    end_time: '',
    time_zone: 'UTC',
    meeting_link: '',
    status: 'scheduled'
  });

  // Edit attendees dialog
  const [editAttendeesDialogOpen, setEditAttendeesDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [attendeeEmails, setAttendeeEmails] = useState('');

  // Expanded accordion states
  const [expanded, setExpanded] = useState('upload');

  // Delete confirmation modal
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState(''); // 'schedule' or 'backlog'

  // Reupload preview modal
  const [reuploadPreviewOpen, setReuploadPreviewOpen] = useState(false);
  const [reuploadFile, setReuploadFile] = useState(null);
  const [reuploadPreview, setReuploadPreview] = useState(null);

  // Schedule import preview modal
  const [scheduleImportPreviewOpen, setScheduleImportPreviewOpen] = useState(false);
  const [scheduleImportFile, setScheduleImportFile] = useState(null);
  const [scheduleImportPreview, setScheduleImportPreview] = useState(null);

  // Backlog preview modal
  const [backlogPreviewOpen, setBacklogPreviewOpen] = useState(false);

  // Fetch backlog items, schedules, and workshop settings on load
  useEffect(() => {
    if (projectId) {
      fetchBacklogItems();
      fetchSchedules();
      fetchWorkshopSettings();
    }
  }, [projectId]);

  const fetchWorkshopSettings = async () => {
    try {
      const response = await fetch(
        `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':5001' : '/api'}/workshops/settings/${projectId}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const settings = await response.json();
        setWorkshopSettings(settings);
      } else {
        console.log('No workshop settings found, using defaults');
        setWorkshopSettings({ time_zone: 'UTC' });
      }
    } catch (error) {
      console.error('Error fetching workshop settings:', error);
      setWorkshopSettings({ time_zone: 'UTC' });
    }
  };

  const fetchBacklogItems = async () => {
    try {
      setLoading(true);
      const items = await gapAnalysisService.getBacklogItems(projectId);
      setBacklogItems(items);

      // Filter items requiring workshops
      const workshopItems = items.filter(
        item => item.workshop_required && ['Y', 'YES'].includes(item.workshop_required.toUpperCase())
      );
      setWorkshopRequiredItems(workshopItems);
    } catch (error) {
      console.error('Error fetching backlog items:', error);
      setError('Failed to fetch backlog items');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedules = async () => {
    try {
      const schedules = await gapAnalysisService.getSchedules(projectId);
      setSchedules(schedules);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      setError(null);
      setUploadMessage(null);

      // Check if items already exist - if so, show reupload preview
      if (backlogItems.length > 0) {
        // Show reupload preview
        const previewResult = await gapAnalysisService.previewBacklogReupload(projectId, file);

        setReuploadFile(file);
        setReuploadPreview(previewResult);
        setReuploadPreviewOpen(true);
        setLoading(false);
      } else {
        // First upload - no preview needed
        const result = await gapAnalysisService.uploadBacklogItemsExcel(projectId, file);

        setUploadMessage({
          type: 'success',
          text: `Successfully uploaded ${result.items.length} backlog items. ${result.workshop_required_count} items require workshops.`
        });

        // Refresh data
        fetchBacklogItems();
        setExpanded('items');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to upload file'
      });
      setLoading(false);
    } finally {
      event.target.value = '';
    }
  };

  const handleConfirmReupload = async () => {
    if (!reuploadFile) return;

    try {
      setLoading(true);
      setReuploadPreviewOpen(false);

      const result = await gapAnalysisService.applyBacklogReupload(projectId, reuploadFile);

      setUploadMessage({
        type: 'success',
        text: `${result.message}. ${result.updated_count} items updated, ${result.new_count} items added.`
      });

      // Clear states
      setReuploadFile(null);
      setReuploadPreview(null);

      // Refresh data
      fetchBacklogItems();
    } catch (error) {
      console.error('Error applying reupload:', error);
      setError(error.response?.data?.error || 'Failed to apply changes');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReupload = () => {
    setReuploadPreviewOpen(false);
    setReuploadFile(null);
    setReuploadPreview(null);
  };

  const handleClearBacklog = () => {
    setDeleteTarget(null);
    setDeleteType('backlog');
    setDeleteDialogOpen(true);
  };

  const handleScheduleWorkshop = (item) => {
    setSelectedItem(item);
    setScheduleForm({
      scheduled_date: '',
      start_time: '',
      end_time: '',
      time_zone: 'UTC',
      meeting_link: '',
      status: 'scheduled'
    });
    setScheduleDialogOpen(true);
  };

  const handleSaveSchedule = async () => {
    if (!scheduleForm.scheduled_date || !scheduleForm.start_time || !scheduleForm.end_time) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);

      // Fetch attendees for this backlog item
      const attendeeResponse = await gapAnalysisService.getBacklogItemAttendees(selectedItem.id, projectId);

      // Extract attendees array from response
      const attendees = attendeeResponse.attendees || [];

      // Log matching information for debugging
      console.log('Scope Item Matching:', {
        backlog_scope_item: attendeeResponse.backlog_scope_item,
        matched_scope_item: attendeeResponse.matched_scope_item,
        matched_module: attendeeResponse.matched_module,
        attendees_count: attendees.length
      });

      // Create workshop name based on the item
      const workshopName = await gapAnalysisService.createWorkshopName(
        projectId,
        selectedItem.best_practice_scope_item || selectedItem.requirement_title
      );

      // Create mapping
      await gapAnalysisService.createWorkshopMapping(workshopName.id, selectedItem.id);

      // Create workshop details with attendees
      const workshop = await gapAnalysisService.createWorkshop({
        project_id: projectId,
        workshop_name_id: workshopName.id,
        objective: selectedItem.requirement_short_description || '',
        attendees: attendees
      });

      // Create schedule
      await gapAnalysisService.createSchedule({
        workshop_id: workshop.id,
        project_id: projectId,
        ...scheduleForm
      });

      setUploadMessage({
        type: 'success',
        text: `Workshop scheduled successfully with ${attendees.length} attendees`
      });
      setScheduleDialogOpen(false);
      fetchSchedules();
      setExpanded('schedules');
    } catch (error) {
      console.error('Error saving schedule:', error);
      setError(error.response?.data?.error || 'Failed to save schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = (scheduleId) => {
    setDeleteTarget(scheduleId);
    setDeleteType('schedule');
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      setDeleteDialogOpen(false);

      if (deleteType === 'backlog') {
        await gapAnalysisService.clearBacklogItems(projectId);
        setUploadMessage({ type: 'success', text: 'All backlog items cleared successfully' });
        fetchBacklogItems();
      } else if (deleteType === 'schedule') {
        await gapAnalysisService.deleteSchedule(deleteTarget);
        setUploadMessage({
          type: 'success',
          text: 'Schedule deleted successfully. You can now reschedule this item from Step 2.'
        });
        fetchSchedules();
      }

      // Clear delete states
      setDeleteTarget(null);
      setDeleteType('');
    } catch (error) {
      console.error('Error deleting:', error);
      setError(`Failed to delete ${deleteType}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
    setDeleteType('');
  };

  const handleEditAttendees = (schedule) => {
    setSelectedSchedule(schedule);
    setAttendeeEmails(schedule.attendee_emails || '');
    setEditAttendeesDialogOpen(true);
  };

  const handleSaveAttendees = async () => {
    if (!selectedSchedule) return;

    try {
      setLoading(true);

      // Parse comma-separated emails
      const emails = attendeeEmails
        .split(',')
        .map(e => e.trim())
        .filter(e => e);

      // Convert to attendee objects
      const attendees = emails.map(email => ({
        email: email,
        name: email.split('@')[0], // Use part before @ as name
        is_mandatory: false
      }));

      // Update workshop with new attendees
      await gapAnalysisService.updateWorkshop(selectedSchedule.workshop_id, {
        attendees: attendees
      });

      setUploadMessage({ type: 'success', text: 'Attendees updated successfully' });
      setEditAttendeesDialogOpen(false);
      fetchSchedules();
    } catch (error) {
      console.error('Error updating attendees:', error);
      setError('Failed to update attendees');
    } finally {
      setLoading(false);
    }
  };

  const handleExportSchedules = async () => {
    try {
      const blob = await gapAnalysisService.exportSchedules(projectId);

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gap_analysis_schedules_${projectId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting schedules:', error);
      setError('Failed to export schedules');
    }
  };

  const handleDownloadSingleIcs = async (scheduleId) => {
    try {
      const blob = await gapAnalysisService.downloadSingleIcs(scheduleId);

      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/calendar' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `workshop_${scheduleId}.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading ICS:', error);
      setError('Failed to download ICS file');
    }
  };

  const handleOpenGoogleCalendar = (schedule) => {
    // Format date for Google Calendar (YYYYMMDD)
    const dateStr = schedule.scheduled_date ? schedule.scheduled_date.replace(/-/g, '') : '';

    // Format times for Google Calendar (HHMMSS)
    const formatTime = (timeStr) => {
      if (!timeStr) return '000000';
      // Handle both HH:MM and HH:MM:SS formats
      const parts = timeStr.split(':');
      const hours = parts[0] || '00';
      const minutes = parts[1] || '00';
      const seconds = parts[2] || '00';
      return `${hours}${minutes}${seconds}`;
    };

    const startTimeStr = formatTime(schedule.start_time);
    const endTimeStr = formatTime(schedule.end_time);

    // Build Google Calendar URL
    const title = encodeURIComponent(schedule.workshop_name || 'Gap Analysis Workshop');
    const details = encodeURIComponent('Gap Analysis Workshop - Fit-to-Standard');
    const location = encodeURIComponent('Online Meeting');

    // Add attendees if available
    let attendeesParam = '';
    if (schedule.attendee_emails) {
      const emails = schedule.attendee_emails.split(',').map(e => e.trim()).filter(e => e);
      if (emails.length > 0) {
        attendeesParam = `&add=${emails.map(e => encodeURIComponent(e)).join(',')}`;
      }
    }

    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dateStr}T${startTimeStr}/${dateStr}T${endTimeStr}&details=${details}&location=${location}${attendeesParam}`;

    window.open(googleCalendarUrl, '_blank');
  };

  const handleImportSchedules = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      setError(null);

      // First preview changes
      const previewResult = await gapAnalysisService.previewScheduleImport(projectId, file);

      if (previewResult.errors && previewResult.errors.length > 0) {
        setError(`Import has errors:\n${previewResult.errors.join('\n')}`);
        setLoading(false);
        return;
      }

      if (!previewResult.changes || previewResult.changes.length === 0) {
        setUploadMessage({ type: 'info', text: 'No changes detected in the imported file' });
        setLoading(false);
        return;
      }

      // Show preview modal
      setScheduleImportFile(file);
      setScheduleImportPreview(previewResult);
      setScheduleImportPreviewOpen(true);
      setLoading(false);
    } catch (error) {
      console.error('Error importing schedules:', error);
      setError(error.response?.data?.error || 'Failed to import schedules');
      setLoading(false);
    } finally {
      event.target.value = '';
    }
  };

  const handleConfirmScheduleImport = async () => {
    if (!scheduleImportFile) return;

    try {
      setLoading(true);
      setScheduleImportPreviewOpen(false);

      // Apply changes
      await gapAnalysisService.applyScheduleImport(projectId, scheduleImportFile);

      setUploadMessage({
        type: 'success',
        text: `Successfully updated ${scheduleImportPreview.changes.length} schedules`
      });

      // Clear states
      setScheduleImportFile(null);
      setScheduleImportPreview(null);

      // Refresh data
      fetchSchedules();
    } catch (error) {
      console.error('Error applying schedule import:', error);
      setError(error.response?.data?.error || 'Failed to apply schedule changes');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelScheduleImport = () => {
    setScheduleImportPreviewOpen(false);
    setScheduleImportFile(null);
    setScheduleImportPreview(null);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleOpenBacklogPreview = () => {
    setBacklogPreviewOpen(true);
  };

  const handleCloseBacklogPreview = () => {
    setBacklogPreviewOpen(false);
  };

  // Download calendar invites
  const handleDownloadAllInvites = async (format) => {
    try {
      setLoading(true);
      setError(null);

      if (format === 'google') {
        // Generate Google Meet links for all schedules
        const result = await gapAnalysisService.generateGoogleMeetLinks(projectId);

        // Create a text file with all Google Meet links
        const linksText = result.schedules.map(s =>
          `${s.workshop_name}\nDate: ${s.scheduled_date} ${s.start_time}-${s.end_time} (${s.time_zone})\nGoogle Meet: ${s.meeting_link}\n---`
        ).join('\n\n');

        const blob = new Blob([linksText], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Gap_Analysis_Google_Meet_Links_${new Date().toISOString().split('T')[0]}.txt`;
        link.click();
        window.URL.revokeObjectURL(url);

        setUploadMessage({
          type: 'success',
          text: 'Google Meet links downloaded successfully'
        });
      } else if (format === 'ics') {
        // Download ICS files
        await gapAnalysisService.downloadAllICS(projectId);

        setUploadMessage({
          type: 'success',
          text: 'ICS calendar files downloaded successfully'
        });
      }

      // Refresh schedules to get updated meeting links
      fetchSchedules();
    } catch (error) {
      console.error('Error downloading calendar invites:', error);
      setError(error.response?.data?.error || 'Failed to download calendar invites');
    } finally {
      setLoading(false);
    }
  };

  // Auto-schedule workshops
  const handleAutoSchedule = async () => {
    // Validate dates
    if (!scheduleConfig.start_date || !scheduleConfig.end_date) {
      setError('Please provide both start and end dates');
      return;
    }

    if (new Date(scheduleConfig.end_date) <= new Date(scheduleConfig.start_date)) {
      setError('End date must be after start date');
      return;
    }

    try {
      setAutoScheduling(true);
      setError(null);

      const result = await gapAnalysisService.autoScheduleWorkshops(projectId, {
        start_date: scheduleConfig.start_date,
        end_date: scheduleConfig.end_date,
        time_zone: workshopSettings?.time_zone || 'UTC'
      });

      setUploadMessage({
        type: 'success',
        text: `Successfully scheduled ${result.scheduled_count} workshops from ${scheduleConfig.start_date} to ${scheduleConfig.end_date}`
      });

      // Refresh schedules
      fetchSchedules();
      setExpanded('schedules');
    } catch (error) {
      console.error('Error auto-scheduling workshops:', error);
      setError(error.response?.data?.error || 'Failed to auto-schedule workshops');
    } finally {
      setAutoScheduling(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#4DB6AC' }}>
          Gap Analysis - Backlog including Delta Requirements
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          The purpose of this document is to capture the delta requirements during the Fit-to-Standard workshops
          in the Explore phase. Preparation of release and sprint plan in late Explore phase. Supports planning
          and management of the delivery of the solution capabilities in Realize phase, provides tool for planning
          & monitoring of the release & sprints.
        </Typography>
      </Box>

      {/* Messages */}
      {uploadMessage && (
        <Alert severity={uploadMessage.type} onClose={() => setUploadMessage(null)} sx={{ mb: 3 }}>
          {uploadMessage.text}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Accordion 1: Upload Backlog */}
      <Accordion expanded={expanded === 'upload'} onChange={handleAccordionChange('upload')} sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <UploadIcon color="primary" />
            <Typography variant="h6">Step 1: Upload Backlog Excel</Typography>
            {backlogItems.length > 0 && (
              <Chip
                label={`${backlogItems.length} items uploaded`}
                color="success"
                size="small"
                icon={<CheckCircleIcon />}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload your Excel file containing the Backlog sheet. The system will automatically parse items where
              "Workshop Required Y/N" is marked as Y or YES.
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<UploadIcon />}
                  disabled={loading}
                >
                  Upload Excel File
                  <input type="file" hidden accept=".xlsx,.xls" onChange={handleFileUpload} />
                </Button>
              </Grid>
              {backlogItems.length > 0 && (
                <>
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<VisibilityIcon />}
                      onClick={handleOpenBacklogPreview}
                      disabled={loading}
                    >
                      Backlog Preview
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleClearBacklog}
                      disabled={loading}
                    >
                      Clear All Items
                    </Button>
                  </Grid>
                </>
              )}
              {loading && (
                <Grid item>
                  <CircularProgress size={24} />
                </Grid>
              )}
            </Grid>

            {backlogItems.length > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Upload Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">{backlogItems.length}</Typography>
                        <Typography variant="body2" color="text.secondary">Total Items</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">{workshopRequiredItems.length}</Typography>
                        <Typography variant="body2" color="text.secondary">Require Workshops</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="info.main">{schedules.length}</Typography>
                        <Typography variant="body2" color="text.secondary">Scheduled</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Accordion 2: Schedule Configuration */}
      <Accordion expanded={expanded === 'config'} onChange={handleAccordionChange('config')} sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SettingsIcon color="primary" />
            <Typography variant="h6">Step 2: Schedule Configuration</Typography>
            {scheduleConfig.start_date && scheduleConfig.end_date && (
              <Chip
                label={`${scheduleConfig.start_date} to ${scheduleConfig.end_date}`}
                color="success"
                size="small"
                icon={<CheckCircleIcon />}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {workshopRequiredItems.length === 0 ? (
            <Alert severity="warning">
              Please upload backlog items in Step 1 first before configuring schedule.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Configure the schedule for auto-generating workshop meetings. The timezone will be automatically
                inherited from your Workshop Schedule settings in the Prepare phase ({workshopSettings?.time_zone || 'UTC'}).
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Schedule Start Date"
                    type="date"
                    value={scheduleConfig.start_date}
                    onChange={(e) => setScheduleConfig(prev => ({ ...prev, start_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    helperText="First date for scheduling workshops"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Schedule End Date"
                    type="date"
                    value={scheduleConfig.end_date}
                    onChange={(e) => setScheduleConfig(prev => ({ ...prev, end_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    helperText="Last date for scheduling workshops"
                  />
                </Grid>
              </Grid>

              <Card sx={{ mt: 3, bgcolor: 'info.light' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>Configuration Summary:</strong>
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2"><strong>Items to Schedule:</strong> {workshopRequiredItems.length}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2"><strong>Timezone:</strong> {workshopSettings?.time_zone || 'UTC'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2">
                        <strong>Already Scheduled:</strong> {schedules.length}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Box sx={{ mt: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Only unscheduled workshop-required items will be scheduled. Existing workshop schedules will not be changed.
                  This action does not use AI.
                </Alert>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleAutoSchedule}
                  disabled={!scheduleConfig.start_date || !scheduleConfig.end_date || autoScheduling || workshopRequiredItems.length === 0}
                >
                  {autoScheduling ? 'Scheduling...' : 'Auto-Schedule Workshops'}
                </Button>
                {autoScheduling && <CircularProgress size={24} sx={{ ml: 2 }} />}
              </Box>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Accordion 3: Auto-Scheduled Workshops */}
      <Accordion expanded={expanded === 'items'} onChange={handleAccordionChange('items')} sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EventIcon color="primary" />
            <Typography variant="h6">Step 3: Workshop Schedules</Typography>
            {schedules.length > 0 && (
              <Chip
                label={`${schedules.length} scheduled`}
                color="primary"
                size="small"
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {schedules.length === 0 ? (
            <Alert severity="info">
              No workshops scheduled yet. Configure dates in Step 2 and click "Auto-Schedule Workshops".
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Item ID</strong></TableCell>
                    <TableCell><strong>Process</strong></TableCell>
                    <TableCell><strong>Scope Item</strong></TableCell>
                    <TableCell><strong>Requirement Title</strong></TableCell>
                    <TableCell><strong>Priority</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workshopRequiredItems.map((item) => {
                    const isScheduled = schedules.some(
                      s => s.workshop_name === (item.best_practice_scope_item || item.requirement_title)
                    );

                    return (
                      <TableRow key={item.id}>
                        <TableCell>{item.item_id || '-'}</TableCell>
                        <TableCell>{item.process || '-'}</TableCell>
                        <TableCell>{item.best_practice_scope_item || '-'}</TableCell>
                        <TableCell>{item.requirement_title || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.priority || 'Not Set'}
                            size="small"
                            color={item.priority?.includes('Must') ? 'error' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{item.status || '-'}</TableCell>
                        <TableCell align="center">
                          {isScheduled ? (
                            <Chip label="Scheduled" color="success" size="small" />
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => handleScheduleWorkshop(item)}
                            >
                              Schedule
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Accordion 4: Download Calendar Invites */}
      <Accordion expanded={expanded === 'download'} onChange={handleAccordionChange('download')} sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CalendarIcon color="primary" />
            <Typography variant="h6">Step 4: Download Calendar Invites</Typography>
            {schedules.length > 0 && (
              <Chip
                label={`${schedules.length} available`}
                color="success"
                size="small"
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {schedules.length === 0 ? (
              <Alert severity="info">
                No workshops scheduled yet. Complete Step 3 to auto-schedule workshops first.
              </Alert>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Download calendar invites for your scheduled workshops. Choose from Google Meet, ICS file, or Excel export options.
                </Typography>

                <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CalendarIcon />}
                    onClick={() => handleDownloadAllInvites('google')}
                    disabled={schedules.length === 0 || loading}
                  >
                    Download Google Meet Links
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadAllInvites('ics')}
                    disabled={schedules.length === 0 || loading}
                  >
                    Download ICS Files
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportSchedules}
                    disabled={schedules.length === 0 || loading}
                  >
                    Export to Excel
                  </Button>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    disabled={schedules.length === 0 || loading}
                  >
                    Import Updated Schedules
                    <input type="file" hidden accept=".xlsx,.xls" onChange={handleImportSchedules} />
                  </Button>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Scheduled Workshops
                </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Workshop Name</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Start Time</strong></TableCell>
                      <TableCell><strong>End Time</strong></TableCell>
                      <TableCell><strong>Time Zone</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Attendee Emails</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schedules.map((schedule) => (
                      <TableRow key={schedule.id}>
                        <TableCell>{schedule.workshop_name || '-'}</TableCell>
                        <TableCell>{schedule.scheduled_date || '-'}</TableCell>
                        <TableCell>{schedule.start_time || '-'}</TableCell>
                        <TableCell>{schedule.end_time || '-'}</TableCell>
                        <TableCell>{schedule.time_zone || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={schedule.status || 'scheduled'}
                            size="small"
                            color={schedule.status === 'completed' ? 'success' : 'primary'}
                          />
                        </TableCell>
                        <TableCell>
                          {schedule.attendee_emails || '-'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleDownloadSingleIcs(schedule.id)}
                            title="Download ICS File"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenGoogleCalendar(schedule)}
                            title="Add to Google Calendar"
                          >
                            <CalendarIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditAttendees(schedule)}
                            title="Edit Attendees"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteSchedule(schedule.id)}
                            title="Delete Schedule"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Schedule Workshop Dialog */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Workshop</DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Workshop for:</Typography>
              <Typography variant="body1" gutterBottom>
                <strong>{selectedItem.best_practice_scope_item || selectedItem.requirement_title}</strong>
              </Typography>
              {selectedItem.requirement_short_description && (
                <Typography variant="body2" color="text.secondary">
                  {selectedItem.requirement_short_description}
                </Typography>
              )}
            </Box>
          )}
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                value={scheduleForm.scheduled_date}
                onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Start Time"
                type="time"
                fullWidth
                value={scheduleForm.start_time}
                onChange={(e) => setScheduleForm({ ...scheduleForm, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End Time"
                type="time"
                fullWidth
                value={scheduleForm.end_time}
                onChange={(e) => setScheduleForm({ ...scheduleForm, end_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Time Zone"
                fullWidth
                value={scheduleForm.time_zone}
                onChange={(e) => setScheduleForm({ ...scheduleForm, time_zone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Meeting Link (optional)"
                fullWidth
                value={scheduleForm.meeting_link}
                onChange={(e) => setScheduleForm({ ...scheduleForm, meeting_link: e.target.value })}
                placeholder="https://meet.google.com/..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSchedule} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Attendees Dialog */}
      <Dialog open={editAttendeesDialogOpen} onClose={() => setEditAttendeesDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Attendees</DialogTitle>
        <DialogContent>
          {selectedSchedule && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Workshop:</Typography>
              <Typography variant="body1" gutterBottom>
                <strong>{selectedSchedule.workshop_name}</strong>
              </Typography>
            </Box>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary" paragraph>
            Enter attendee email addresses separated by commas. If no attendees were found from Prepare &gt; Workshop Schedule,
            you can add them manually here.
          </Typography>
          <TextField
            label="Attendee Emails"
            fullWidth
            multiline
            rows={4}
            value={attendeeEmails}
            onChange={(e) => setAttendeeEmails(e.target.value)}
            placeholder="email1@example.com, email2@example.com, email3@example.com"
            helperText="Separate multiple emails with commas"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAttendeesDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveAttendees} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save Attendees'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>
            {deleteType === 'backlog'
              ? 'Are you sure you want to clear all backlog items? This action cannot be undone.'
              : 'Are you sure you want to delete this schedule? This will remove the workshop and allow you to reschedule it again.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reupload Preview Dialog */}
      <Dialog
        open={reuploadPreviewOpen}
        onClose={handleCancelReupload}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          Preview Changes - Re-upload Backlog
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {reuploadPreview && (
            <Box>
              {/* Summary */}
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Total Changes:</strong> {reuploadPreview.total_changes} items will be updated
                </Typography>
                <Typography variant="body2">
                  <strong>New Items:</strong> {reuploadPreview.total_new_items} items will be added
                </Typography>
              </Alert>

              {/* Errors */}
              {reuploadPreview.errors && reuploadPreview.errors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Errors found:</strong>
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {reuploadPreview.errors.map((error, idx) => (
                      <li key={idx}><Typography variant="body2">{error}</Typography></li>
                    ))}
                  </ul>
                </Alert>
              )}

              {/* Changes Table */}
              {reuploadPreview.changes && reuploadPreview.changes.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Items with Changes ({reuploadPreview.changes.length})
                  </Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Item ID</strong></TableCell>
                          <TableCell><strong>Row</strong></TableCell>
                          <TableCell><strong>Field</strong></TableCell>
                          <TableCell><strong>Old Value</strong></TableCell>
                          <TableCell><strong>New Value</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reuploadPreview.changes.map((change, idx) => (
                          change.fields_changed.map((field, fieldIdx) => (
                            <TableRow key={`${idx}-${fieldIdx}`}>
                              {fieldIdx === 0 && (
                                <>
                                  <TableCell rowSpan={change.fields_changed.length}>
                                    {change.item_id}
                                  </TableCell>
                                  <TableCell rowSpan={change.fields_changed.length}>
                                    {change.row}
                                  </TableCell>
                                </>
                              )}
                              <TableCell>{field.field}</TableCell>
                              <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {field.old_value || '(empty)'}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', bgcolor: 'success.light' }}>
                                {field.new_value || '(empty)'}
                              </TableCell>
                            </TableRow>
                          ))
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* New Items Table */}
              {reuploadPreview.new_items && reuploadPreview.new_items.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    New Items ({reuploadPreview.new_items.length})
                  </Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Row</strong></TableCell>
                          <TableCell><strong>Item ID</strong></TableCell>
                          <TableCell><strong>Process</strong></TableCell>
                          <TableCell><strong>Best Practice Scope Item</strong></TableCell>
                          <TableCell><strong>Priority</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reuploadPreview.new_items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{item.row}</TableCell>
                            <TableCell>{item.item_id}</TableCell>
                            <TableCell>{item.process}</TableCell>
                            <TableCell>{item.best_practice_scope_item}</TableCell>
                            <TableCell>{item.priority}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* No Changes */}
              {reuploadPreview.total_changes === 0 && reuploadPreview.total_new_items === 0 && (
                <Alert severity="info">
                  No changes detected. The uploaded file has the same data as what's currently in the system.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReupload} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReupload}
            color="primary"
            variant="contained"
            disabled={loading || (reuploadPreview && reuploadPreview.errors && reuploadPreview.errors.length > 0) || (reuploadPreview && reuploadPreview.total_changes === 0 && reuploadPreview.total_new_items === 0)}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {loading ? 'Applying...' : 'Apply Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Import Preview Dialog */}
      <Dialog
        open={scheduleImportPreviewOpen}
        onClose={handleCancelScheduleImport}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          Preview Changes - Import Workshop Schedules
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {scheduleImportPreview && (
            <Box>
              {/* Summary */}
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Total Changes:</strong> {scheduleImportPreview.changes?.length || 0} schedules will be updated
                </Typography>
              </Alert>

              {/* Errors */}
              {scheduleImportPreview.errors && scheduleImportPreview.errors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Errors found:</strong>
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {scheduleImportPreview.errors.map((error, idx) => (
                      <li key={idx}><Typography variant="body2">{error}</Typography></li>
                    ))}
                  </ul>
                </Alert>
              )}

              {/* Changes Table */}
              {scheduleImportPreview.changes && scheduleImportPreview.changes.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Schedule Changes ({scheduleImportPreview.changes.length})
                  </Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Workshop Name</strong></TableCell>
                          <TableCell><strong>Row</strong></TableCell>
                          <TableCell><strong>Field</strong></TableCell>
                          <TableCell><strong>Old Value</strong></TableCell>
                          <TableCell><strong>New Value</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {scheduleImportPreview.changes.map((change, idx) => (
                          change.fields.map((field, fieldIdx) => (
                            <TableRow key={`${idx}-${fieldIdx}`}>
                              {fieldIdx === 0 && (
                                <>
                                  <TableCell rowSpan={change.fields.length}>
                                    {change.workshop_name}
                                  </TableCell>
                                  <TableCell rowSpan={change.fields.length}>
                                    {change.row}
                                  </TableCell>
                                </>
                              )}
                              <TableCell>{field.field}</TableCell>
                              <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {field.old || '(empty)'}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', bgcolor: 'success.light' }}>
                                {field.new || '(empty)'}
                              </TableCell>
                            </TableRow>
                          ))
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* No Changes */}
              {(!scheduleImportPreview.changes || scheduleImportPreview.changes.length === 0) && (
                <Alert severity="info">
                  No changes detected. The imported file has the same data as the current schedules.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelScheduleImport} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmScheduleImport}
            color="primary"
            variant="contained"
            disabled={
              loading ||
              (scheduleImportPreview && scheduleImportPreview.errors && scheduleImportPreview.errors.length > 0) ||
              (scheduleImportPreview && (!scheduleImportPreview.changes || scheduleImportPreview.changes.length === 0))
            }
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {loading ? 'Applying...' : 'Apply Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backlog Preview Dialog */}
      <Dialog
        open={backlogPreviewOpen}
        onClose={handleCloseBacklogPreview}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          Backlog Preview - All Uploaded Items ({backlogItems.length} items)
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {backlogItems.length === 0 ? (
            <Alert severity="info">
              No backlog items found. Please upload an Excel file first.
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>Item ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Process</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Best Practice Scope Item</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Requirement Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Requirement Title</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Date Identified</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Workshop Required</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 250 }}>Short Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Client Team Lead</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Seleccion Resource</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Update Notes</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 250 }}>Proposed Solution</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 250 }}>Alternative Solution</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Org Structure Impact</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Target Release</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>WRICEF ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {backlogItems.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.item_id || '-'}</TableCell>
                      <TableCell>{item.process || '-'}</TableCell>
                      <TableCell>{item.best_practice_scope_item || '-'}</TableCell>
                      <TableCell>{item.source || '-'}</TableCell>
                      <TableCell>{item.requirement_type || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.priority || 'Not Set'}
                          size="small"
                          color={item.priority?.includes('Must') || item.priority?.includes('High') ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{item.requirement_title || '-'}</TableCell>
                      <TableCell>{item.date_identified || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.workshop_required || 'N'}
                          size="small"
                          color={['Y', 'YES'].includes(item.workshop_required?.toUpperCase()) ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                        {item.requirement_short_description || '-'}
                      </TableCell>
                      <TableCell>{item.client_team_lead || '-'}</TableCell>
                      <TableCell>{item.seleccion_primary_resource || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.status || 'Not Set'}
                          size="small"
                          color="default"
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                        {item.update_notes || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                        {item.proposed_solution || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                        {item.alternative_solution || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                        {item.organizational_structure_impact || '-'}
                      </TableCell>
                      <TableCell>{item.target_release || '-'}</TableCell>
                      <TableCell>{item.wricef_id || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {backlogItems.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Note:</strong> This preview shows all fields from the uploaded Excel file.
                You can scroll horizontally to view all columns. Items marked with "Workshop Required = Y"
                will appear in Step 2 for scheduling.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBacklogPreview} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GapAnalysisPage;
